import {
  Alert,
  Anchor,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Container,
  Grid,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
  LuArrowLeft,
  LuCalendarClock,
  LuCircleCheck,
  LuCircleStop,
  LuListChecks,
  LuPlay,
  LuTrash2,
  LuTriangleAlert,
  LuUser,
} from 'react-icons/lu'
import { StatusBadge } from '@/frontend/components/marina/StatusBadge'
import {
  type AssignmentStatus,
  useAssignment,
  useDeleteAssignment,
  useUpdateAssignment,
} from '@/frontend/hooks/useAssignments'
import { useCanDelete, useCanWrite } from '@/frontend/hooks/useAuth'

export const Route = createFileRoute('/dashboard/facility/assignments/$id')({
  component: AssignmentDetail,
})

const CATEGORY_LABEL: Record<string, string> = {
  CUSTOMER_SUPPORT: 'Layanan Pelanggan',
  MAINTENANCE: 'Pemeliharaan',
  CLEANING: 'Kebersihan',
  ADMIN: 'Administrasi',
  SECURITY: 'Keamanan',
  OTHER: 'Lainnya',
}

function AssignmentDetail() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const canWrite = useCanWrite()
  const canDelete = useCanDelete()
  const { data, isLoading } = useAssignment(id)
  const updateMut = useUpdateAssignment()
  const deleteMut = useDeleteAssignment()

  if (isLoading) {
    return (
      <Container size="lg" py="md">
        <Text c="dimmed">Loading…</Text>
      </Container>
    )
  }
  if (!data?.assignment) {
    return (
      <Container size="lg" py="md">
        <Stack gap="md" align="center" py="xl">
          <ThemeIcon variant="light" color="gray" size="xl" radius="xl">
            <LuTriangleAlert size={22} />
          </ThemeIcon>
          <Text fw={600}>Tugas tidak ditemukan</Text>
          <Button component={Link} to="/dashboard/facility/assignments" leftSection={<LuArrowLeft size={14} />}>
            Kembali ke daftar tugas
          </Button>
        </Stack>
      </Container>
    )
  }

  const a = data.assignment

  const changeStatus = (next: AssignmentStatus, label: string) => {
    updateMut.mutate(
      { id, data: { status: next } },
      {
        onSuccess: () =>
          notifications.show({
            color: 'teal',
            title: 'Status diperbarui',
            message: `Tugas ditandai sebagai ${label}`,
          }),
        onError: (e) =>
          notifications.show({
            color: 'red',
            title: 'Gagal',
            message: (e as Error).message ?? 'Tidak dapat memperbarui',
          }),
      },
    )
  }

  const handleDelete = () => {
    modals.openConfirmModal({
      title: 'Hapus tugas?',
      children: <Text size="sm">Tindakan ini tidak dapat dibatalkan. Tugas akan dihapus permanen.</Text>,
      labels: { confirm: 'Hapus', cancel: 'Batal' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteMut.mutate(id, {
          onSuccess: () => {
            notifications.show({ color: 'teal', title: 'Terhapus', message: 'Tugas telah dihapus' })
            navigate({ to: '/dashboard/facility/assignments' })
          },
          onError: (e) =>
            notifications.show({
              color: 'red',
              title: 'Gagal',
              message: (e as Error).message ?? 'Tidak dapat menghapus',
            }),
        })
      },
    })
  }

  const status = a.status ?? 'PENDING'
  const isTerminal = status === 'COMPLETED' || status === 'CANCELLED'

  return (
    <Container size="lg" py="md">
      <Stack gap="lg">
        <Breadcrumbs>
          <Anchor component={Link} to="/dashboard/facility/assignments" c="dimmed">
            {t('nav.tasks', { defaultValue: 'Tugas' })}
          </Anchor>
          <span>{a.title}</span>
        </Breadcrumbs>

        <Card padding="lg" radius="md" withBorder>
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group gap="sm" align="flex-start" wrap="nowrap">
              <ThemeIcon variant="light" color="laut" size="lg" radius="md">
                <LuListChecks size={18} />
              </ThemeIcon>
              <div>
                <Title order={3} ff="var(--font-display)" fw={600} mb={4}>
                  {a.title}
                </Title>
                <Group gap="xs">
                  <StatusBadge status={status} kind="assignmentStatus" upper />
                  {a.priority && <StatusBadge status={a.priority} kind="assignmentPriority" upper />}
                  {a.category && (
                    <Badge variant="light" color="gray" radius="sm">
                      {CATEGORY_LABEL[a.category] ?? a.category}
                    </Badge>
                  )}
                </Group>
              </div>
            </Group>
            {canDelete && (
              <Button
                variant="subtle"
                color="red"
                size="xs"
                leftSection={<LuTrash2 size={14} />}
                onClick={handleDelete}
                loading={deleteMut.isPending}
              >
                Hapus
              </Button>
            )}
          </Group>
        </Card>

        <Grid>
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Stack gap="md">
              <Card padding="lg" radius="md" withBorder>
                <Text fw={600} mb="sm">
                  Deskripsi
                </Text>
                {a.description ? (
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {a.description}
                  </Text>
                ) : (
                  <Text size="sm" c="dimmed">
                    Tidak ada deskripsi.
                  </Text>
                )}
              </Card>

              {!isTerminal && canWrite && (
                <Card padding="lg" radius="md" withBorder>
                  <Text fw={600} mb="sm">
                    Ubah Status
                  </Text>
                  <Group gap="xs" wrap="wrap">
                    {status === 'PENDING' && (
                      <Button
                        size="xs"
                        color="laut"
                        leftSection={<LuPlay size={13} />}
                        onClick={() => changeStatus('IN_PROGRESS', 'Sedang Dikerjakan')}
                        loading={updateMut.isPending}
                      >
                        Mulai
                      </Button>
                    )}
                    {status === 'IN_PROGRESS' && (
                      <>
                        <Button
                          size="xs"
                          color="teal"
                          leftSection={<LuCircleCheck size={13} />}
                          onClick={() => changeStatus('COMPLETED', 'Selesai')}
                          loading={updateMut.isPending}
                        >
                          Selesaikan
                        </Button>
                        <Button
                          size="xs"
                          variant="light"
                          color="orange"
                          leftSection={<LuTriangleAlert size={13} />}
                          onClick={() => changeStatus('BLOCKED', 'Tertahan')}
                          loading={updateMut.isPending}
                        >
                          Tahan
                        </Button>
                      </>
                    )}
                    {status === 'BLOCKED' && (
                      <Button
                        size="xs"
                        color="laut"
                        leftSection={<LuPlay size={13} />}
                        onClick={() => changeStatus('IN_PROGRESS', 'Sedang Dikerjakan')}
                        loading={updateMut.isPending}
                      >
                        Lanjutkan
                      </Button>
                    )}
                    <Button
                      size="xs"
                      variant="subtle"
                      color="red"
                      leftSection={<LuCircleStop size={13} />}
                      onClick={() => changeStatus('CANCELLED', 'Dibatalkan')}
                      loading={updateMut.isPending}
                    >
                      Batalkan
                    </Button>
                  </Group>
                </Card>
              )}

              {isTerminal && (
                <Alert
                  variant="light"
                  color={status === 'COMPLETED' ? 'teal' : 'gray'}
                  icon={<LuCircleCheck size={16} />}
                >
                  Tugas sudah {status === 'COMPLETED' ? 'selesai' : 'dibatalkan'}. Tidak ada tindakan lebih lanjut.
                </Alert>
              )}
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 5 }}>
            <Card padding="lg" radius="md" withBorder>
              <Text fw={600} mb="md">
                Detail
              </Text>
              <Stack gap="md">
                <DetailRow icon={<LuUser size={14} />} label="Ditugaskan ke" value={a.assignedStaff?.name ?? '—'} />
                <DetailRow
                  icon={<LuCalendarClock size={14} />}
                  label="Mulai"
                  value={a.startDate ? dayjs(a.startDate).format('DD MMM YYYY · HH:mm') : '—'}
                />
                <DetailRow
                  icon={<LuCalendarClock size={14} />}
                  label="Batas waktu"
                  value={a.deadline ? dayjs(a.deadline).format('DD MMM YYYY · HH:mm') : '—'}
                  highlight={
                    a.deadline && !isTerminal && dayjs(a.deadline).isBefore(dayjs())
                      ? { color: 'red.6', suffix: 'Terlambat' }
                      : undefined
                  }
                />
                <DetailRow
                  label="Referensi"
                  value={a.referenceType && a.referenceId ? `${a.referenceType} · ${a.referenceId.slice(0, 8)}…` : '—'}
                />
                <DetailRow label="Dibuat" value={dayjs(a.createdAt).format('DD MMM YYYY · HH:mm')} />
                <DetailRow label="Diperbarui" value={dayjs(a.updatedAt).format('DD MMM YYYY · HH:mm')} />
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>
      </Stack>
    </Container>
  )
}

function DetailRow({
  icon,
  label,
  value,
  highlight,
}: {
  icon?: React.ReactNode
  label: string
  value: string
  highlight?: { color: string; suffix?: string }
}) {
  return (
    <div>
      <Group gap={6} mb={2}>
        {icon && <span style={{ color: 'var(--mantine-color-dimmed)', display: 'inline-flex' }}>{icon}</span>}
        <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
          {label}
        </Text>
      </Group>
      <Text size="sm" c={highlight?.color}>
        {value}
        {highlight?.suffix && (
          <Text component="span" size="xs" c={highlight.color} ml={6} fw={600}>
            ({highlight.suffix})
          </Text>
        )}
      </Text>
    </div>
  )
}
